import {
  Button,
  ButtonsWrapper,
  CheckboxSelect,
  Form,
  Modal,
  SelectInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX, GanttAccessRightsEnum } from '@thinknet-gmbh/platform-common';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetCompanyPositions } from 'src/hooks/company-positions/get-company-positions.hook';
import { useCreateInternalMember } from 'src/hooks/members/create-internal-member.hook';
import { useGetPermissionsGroups } from 'src/hooks/permission-group/get-permissions-groups.hook';
import {
  FieldContainer,
  SelectLabel,
} from 'src/pages/settings/components/user-management/components/change-user-permission/change-user-permission.styles';
import { useGetGantts } from 'src/hooks/gantts/get-gantts.hook';
import { IGanttsAccessPayload } from 'src/pages/settings/components/user-management/components/change-user-permission/change-user-permission.interfaces';
import { mapBuilder } from 'src/common/helpers/common.helpers';
import { GanttRightsListSelect } from 'src/pages/settings/components/user-management/components/change-user-permission/gantt-rights-list-select';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';

import { AddAdditionalLocations } from './components/add-additional-locations';

const CreateUserModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(['common', 'settings']);
  const [form] = Form.useForm();

  const companyId = useGetCurrentCompanyId();
  const { companyPositions } = useGetCompanyPositions({ companyId, deleted: false });
  const { permissionsGroups } = useGetPermissionsGroups(companyId);
  const {
    member: { ownerCompany },
  } = useGetCurrentMemberAccount();
  const { loading, gantts } = useGetGantts();
  const { createInternalMember, loading: createLoading } = useCreateInternalMember({
    onSuccess: onClose,
  });
  const [ganttsAccessPayload, setGanttsAccessPayload] = useState<IGanttsAccessPayload[]>([]);
  const onFinish = (values: any) => {
    const { additionalLocations, location, ...data } = values;

    createInternalMember({
      variables: {
        payload: { ...data, companyId, ganttsAccess: ganttsAccessPayload, ownerCompany },
      },
    });
  };

  const onRightChange = (index: number) => (newRight: string) =>
    setGanttsAccessPayload((prevData: IGanttsAccessPayload[]) => {
      const newData = [...prevData];

      newData[index].accessRight = newRight;

      return newData;
    });

  const onGanttRightRemove = (ganttId: string) => () =>
    setGanttsAccessPayload(ganttsAccessPayload.filter((doc: any) => doc.ganttId !== ganttId));

  return (
    <Modal
      title={t('settings:userManagementSettings.labels.addANewUser')}
      onClose={onClose}
      isOpened
    >
      <Form layout="vertical" onFinish={onFinish} form={form}>
        <Form.Item
          name="firstName"
          label={t('common:labels.firstName')}
          rules={[
            {
              required: true,
              message: t('common:validation.error.firstNameRequired'),
            },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput placeholder="Bill" maxLength={50} />
        </Form.Item>
        <Form.Item
          name="lastName"
          label={t('common:labels.lastName')}
          rules={[
            {
              required: true,
              message: t('common:validation.error.lastNameRequired'),
            },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput placeholder="Sanders" maxLength={50} />
        </Form.Item>
        <Form.Item name="position" label={t('settings:userManagementSettings.labels.position')}>
          <SelectInput placeholder="HR Manager">
            {companyPositions.map((position: any) => (
              <SelectInput.Option key={position._id} value={position._id}>
                {position.name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="email"
          label={t('common:labels.email')}
          rules={[
            {
              required: true,
              message: t('common:validation.error.emailRequired'),
            },
            {
              type: 'email',
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput placeholder="bill.sanders@gmail.com" maxLength={50} />
        </Form.Item>
        <Form.Item
          name="permissionGroupId"
          label={t('settings:userManagementSettings.labels.permissionGroup')}
          rules={[
            { required: true, message: t('common:validation.error.permissionGroupRequired') },
          ]}
        >
          <SelectInput
            placeholder={t('settings:userManagementSettings.labels.selectPermissionGroup')}
          >
            {permissionsGroups.map((group: any) => (
              <SelectInput.Option key={group._id} value={group._id}>
                {group.name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="location"
          label={t('common:labels.location')}
          rules={[{ required: true, message: t('common:validation.error.locationRequired') }]}
        >
          <TextInput placeholder="8558 Green Rd." maxLength={100} />
        </Form.Item>
        <AddAdditionalLocations />

        <FieldContainer>
          <SelectLabel htmlFor="ganttsAccess">
            {t('settings:userManagementSettings.labels.availableGantts')}
          </SelectLabel>
          <CheckboxSelect
            showSearch
            id="ganttsAccess"
            loading={loading}
            placeholder={t('common:placeholders.selectFromList')}
            optionData={gantts.map(({ _id, name }: any) => ({ value: _id, label: name }))}
            value={ganttsAccessPayload.map(({ ganttId }) => ganttId)}
            onChange={ganttIds =>
              setGanttsAccessPayload(prevData => {
                const prevDataMap = mapBuilder(prevData, 'ganttId');

                return ganttIds.map((ganttId: string) => ({
                  ganttId,
                  accessRight: prevDataMap[ganttId]
                    ? prevDataMap[ganttId].accessRight
                    : GanttAccessRightsEnum.EDIT,
                }));
              })
            }
          />
        </FieldContainer>

        {ganttsAccessPayload.map(({ ganttId, accessRight }: any, index) => {
          const { name } = gantts.find((gantt: any) => gantt._id === ganttId) || {};

          return (
            <GanttRightsListSelect
              key={ganttId}
              selectedRight={accessRight}
              isViewSeparator={index !== ganttsAccessPayload.length - 1}
              onRightChange={onRightChange(index)}
              onGanttRightRemove={onGanttRightRemove(ganttId)}
              ganttName={name}
            />
          );
        })}
        <ButtonsWrapper marginTop={16}>
          <Button type="primary" htmlType="submit" loading={createLoading}>
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
